import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Alert,
    AlertTitle,
    Divider
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from '../../utils/axios';
import { recaptchaSiteKey } from '../../config';

const Register = () => {
    const navigate = useNavigate();
    const recaptchaRef = useRef(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [registered, setRegistered] = useState(false);
    const [registeredEmail, setRegisteredEmail] = useState('');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const resetRecaptcha = () => {
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        if (!recaptchaToken) {
            setError('Please complete the reCAPTCHA verification');
            setLoading(false);
            return;
        }

        try {
            const response = await axios.post('/api/users/register', {
                name: formData.name,
                email: formData.email,
                password: formData.password,
                recaptchaToken
            });

            setSuccess(response.data.message || 'Registration successful! Please check your email to verify your account.');
            setRegistered(true);
            setRegisteredEmail(formData.email);

            setFormData({
                name: '',
                email: '',
                password: '',
                confirmPassword: ''
            });
            resetRecaptcha();
        } catch (err) {
            setError(err.response?.data?.message || 'Registration failed');
            resetRecaptcha();
        } finally {
            setLoading(false);
        }
    };

    const handleLoginClick = () => {
        navigate('/login');
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                {!registered ? (
                    <>
                        <Typography variant="h4" component="h1" gutterBottom align="center">
                            Register
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            <TextField
                                fullWidth
                                label="Name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                margin="normal"
                                required
                            />
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                margin="normal"
                                required
                            />
                            <TextField
                                fullWidth
                                label="Password"
                                name="password"
                                type="password"
                                value={formData.password}
                                onChange={handleChange}
                                margin="normal"
                                required
                            />
                            <TextField
                                fullWidth
                                label="Confirm Password"
                                name="confirmPassword"
                                type="password"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                margin="normal"
                                required
                            />
                            {recaptchaSiteKey ? (
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        sitekey={recaptchaSiteKey}
                                        onChange={(token) => setRecaptchaToken(token)}
                                        onExpired={resetRecaptcha}
                                    />
                                </Box>
                            ) : (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    reCAPTCHA is not configured. Set REACT_APP_RECAPTCHA_SITE_KEY.
                                </Alert>
                            )}
                            <Box sx={{ mt: 3 }}>
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    disabled={loading || !recaptchaToken}
                                >
                                    {loading ? 'Registering...' : 'Register'}
                                </Button>
                            </Box>
                        </form>
                    </>
                ) : (
                    <Box sx={{ textAlign: 'center' }}>
                        <Alert severity="success" sx={{ mb: 3 }}>
                            <AlertTitle>Registration Successful!</AlertTitle>
                            {success}
                        </Alert>

                        <Typography variant="h5" gutterBottom>
                            Verification Required
                        </Typography>

                        <Typography variant="body1" paragraph>
                            We've sent a verification email to: <strong>{registeredEmail}</strong>
                        </Typography>

                        <Typography variant="body2" paragraph>
                            Please check your inbox (and spam folder) and click the verification link to activate your account.
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="body2" paragraph>
                            Didn't receive the email?
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                            <Button
                                component={Link}
                                to={`/resend-verification?email=${encodeURIComponent(registeredEmail)}`}
                                variant="outlined"
                                color="primary"
                            >
                                Resend Verification
                            </Button>

                            <Button
                                onClick={handleLoginClick}
                                variant="contained"
                                color="primary"
                            >
                                Go to Login
                            </Button>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default Register;
